import { DashboardContent } from 'src/layouts/dashboard';
import { useTranslate } from 'react-admin';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import ErrorPage403 from 'src/pages/error/403';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import GetAppIcon from '@mui/icons-material/GetApp';
import { useEffect, useState } from 'react';
import useEventParticipants from './useEventParticipants';
import { downloadEventParticipantsCSV } from 'src/pages/admin/services/whitelistService';

type OrderField = 'REGISTERED_AT' | 'CUMULATIVE_VOLUME' | 'TICKETS' | 'TOTAL_REWARDS';
type OrderDirection = 'ASC' | 'DESC';

const STATUS_OPTIONS = [
  { value: 'candidate', label: 'Candidate' },
  { value: 'unlocked', label: 'Unlocked' },
];

const View = () => {
  const translate = useTranslate();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [orderField, setOrderField] = useState<OrderField>('REGISTERED_AT');
  const [orderDirection, setOrderDirection] = useState<OrderDirection>('DESC');

  const { retrieve, records, total } = useEventParticipants();

  useEffect(() => {
    retrieve(
      page + 1,
      rowsPerPage,
      search || undefined,
      selectedStatuses.length > 0 ? selectedStatuses : undefined,
      orderField,
      orderDirection
    );
  }, [page, rowsPerPage, search, selectedStatuses, orderField, orderDirection, retrieve]);

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(0);
  };

  const handleStatusChange = (e: SelectChangeEvent<string[]>) => {
    const value = e.target.value;
    setSelectedStatuses(typeof value === 'string' ? value.split(',') : value);
    setPage(0);
  };

  const handleSort = (field: OrderField) => {
    if (orderField === field) {
      setOrderDirection((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setOrderField(field);
      setOrderDirection('DESC');
    }
    setPage(0);
  };

  const handleExportCSV = async () => {
    try {
      const response = await downloadEventParticipantsCSV(
        search || undefined,
        undefined,
        orderField,
        orderDirection
      );
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.setAttribute('download', 'event-participants.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.log('export error: ', error);
    }
  };

  const formatNumber = (value: number | string | null | undefined) => {
    if (value == null || value === '') return '-';
    const num = Number(value);
    return Number.isNaN(num) ? String(value) : num.toLocaleString();
  };

  const formatDate = (value: string | number | null | undefined) => {
    if (!value) return '-';
    return new Date(typeof value === 'string' ? value : Number(value)).toLocaleString();
  };

  const getTotalReward = (row: (typeof records)[0]) => {
    const total = Number(row.rewards?.totalUsdc) || 0;
    return total >= 0 ? total.toLocaleString() : '-';
  };

  const getStatusColor = (s: string): 'warning' | 'success' | 'info' | 'default' => {
    const lower = s?.toLowerCase();
    if (lower === 'unlocked') return 'success';
    if (lower === 'candidate') return 'warning';
    if (lower === 'selected') return 'info';
    return 'default';
  };

  const sortDir = (field: OrderField): 'asc' | 'desc' =>
    orderField === field ? (orderDirection === 'ASC' ? 'asc' : 'desc') : 'desc';

  const SortCell = ({ field, label, align = 'right' }: { field: OrderField; label: string; align?: 'right' | 'left' }) => (
    <TableCell align={align} sortDirection={orderField === field ? sortDir(field) : false}>
      <TableSortLabel
        active={orderField === field}
        direction={sortDir(field)}
        onClick={() => handleSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <DashboardContent>
      <PermissionGuard resource="whitelist-event-participants" action="read" fallback={<ErrorPage403 />}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" fontWeight="medium">
            {translate('whitelist.eventParticipants.title')}
          </Typography>
          <PermissionGuard resource="whitelist-event-participants" action="export" fallback={null}>
            <Button variant="outlined" startIcon={<GetAppIcon />} onClick={handleExportCSV}>
              {translate('whitelist.exportCSV')}
            </Button>
          </PermissionGuard>
        </Box>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <TextField
              size="small"
              placeholder={translate('whitelist.eventParticipants.searchPlaceholder')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              sx={{ flex: 1, minWidth: 260 }}
            />

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>{translate('whitelist.eventParticipants.status')}</InputLabel>
              <Select
                multiple
                value={selectedStatuses}
                onChange={handleStatusChange}
                input={<OutlinedInput label={translate('whitelist.eventParticipants.status')} />}
                renderValue={(selected) =>
                  selected
                    .map((v) => STATUS_OPTIONS.find((o) => o.value === v)?.label ?? v)
                    .join(', ')
                }
              >
                {STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    <Checkbox checked={selectedStatuses.includes(opt.value)} />
                    <ListItemText primary={opt.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch}>
              {translate('whitelist.eventParticipants.search')}
            </Button>
          </Stack>
        </Paper>

        <TableContainer component={Paper}>
          <Table size="small" sx={{ minWidth: 1200 }}>
            <TableHead>
              <TableRow sx={{
                  backgroundColor: '#fff',
                  '& th': {
                    backgroundColor: '#fff',
                    fontWeight: 600,
                  },
                }}>
                <TableCell>{translate('whitelist.eventParticipants.walletAddress')}</TableCell>
                <TableCell>{translate('whitelist.eventParticipants.proxyWalletAddress')}</TableCell>
                <TableCell>{translate('whitelist.eventParticipants.email')}</TableCell>
                <TableCell align="center">
                  {translate('whitelist.eventParticipants.status')}
                </TableCell>
                <SortCell
                  field="CUMULATIVE_VOLUME"
                  label={translate('whitelist.eventParticipants.predictionVolume')}
                />
                <TableCell align="right">
                  {translate('whitelist.eventParticipants.validInvitee')}
                </TableCell>
                <SortCell
                  field="TICKETS"
                  label={translate('whitelist.eventParticipants.tickets')}
                />
                <SortCell
                  field="TOTAL_REWARDS"
                  label={translate('whitelist.eventParticipants.totalRewards')}
                />
                <SortCell
                  field="REGISTERED_AT"
                  label={translate('whitelist.eventParticipants.registrationTime')}
                  align="left"
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {records.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {translate('whitelist.eventParticipants.noData')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                records.map((row) => (
                  <TableRow key={row.userId} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {row.walletAddress || '-'}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {row.proxyWalletAddress || '-'}
                    </TableCell>
                    <TableCell>{row.email || '-'}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={row.status}
                        color={getStatusColor(row.status)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">{formatNumber(row.cumulativeVolumeUsdc)}</TableCell>
                    <TableCell align="right">{formatNumber(row.validReferralCount)}</TableCell>
                    <TableCell align="right">{formatNumber(row.ticketsTotal)}</TableCell>
                    <TableCell align="right">{getTotalReward(row)} USDT</TableCell>
                    <TableCell>{formatDate(row.registeredAt)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
          />
        </TableContainer>
      </Box>
      </PermissionGuard>
    </DashboardContent>
  );
};

export default View;
